import { useMemo } from 'react';

import { graphql } from '@/__generated__';
import { PackageResourceType } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';

export const GetAppliedPackageNames = graphql(/* GraphQL */ `
  query AppliedPackageNames(
    $environmentId: BigInt!
    $resourceType: PackageResourceType!
  ) {
    environment(id: $environmentId) {
      dbtProjectName
      applied {
        lastUpdatedAt
        packages(resource: $resourceType)
      }
    }
  }
`);

type AppliedPackageNamesParams = {
  environmentId: number | undefined;
  resourceType: PackageResourceType;
  pause?: boolean;
};

export type PackageInfo = {
  current: string | undefined;
  packages: string[] | null;
  fetching: boolean;
};

export const useAppliedPackageNames = ({
  environmentId = 0,
  resourceType,
  pause,
}: AppliedPackageNamesParams): PackageInfo => {
  const query = useQueryStable({
    query: GetAppliedPackageNames,
    variables: {
      environmentId,
      resourceType,
    },
    pause: pause || !environmentId,
  });

  useCacheMonitor({ applied: query[0].data?.environment.applied?.lastUpdatedAt });

  const packages: string[] | null = useMemo(() => {
    if (query[0].error || query[0].fetching || query[0].data == null) {
      return null;
    }
    const packages = query[0].data.environment.applied?.packages || [];
    return packages.sort();
  }, [query]);

  return {
    current: query[0].data?.environment.dbtProjectName ?? undefined,
    packages,
    fetching: query[0].fetching,
  };
};
